import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFragrances } from '../../contexts/FragranceContext';
import { Fragrance } from './FragranceCard';
import { ozToMl, mlToOz } from '../../utils/sizeConversion';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Slider from '../ui/Slider';

interface FragranceFormProps {
  fragrance?: Fragrance | null;
  onClose: () => void;
  onSuccess: () => void;
}

const FragranceForm: React.FC<FragranceFormProps> = ({ fragrance, onClose, onSuccess }) => {
  const { createFragrance, updateFragrance, getAutocompleteSuggestions, isLoading } = useFragrances();
  
  const [formData, setFormData] = useState({
    house: '',
    name: '',
    concentration: '',
    size_oz: 0,
    size_ml: 0,
    notes_top: '',
    notes_middle: '',
    notes_base: '',
    is_clone: false,
    original_fragrance: '',
    winter_rating: 0,
    spring_rating: 0,
    summer_rating: 0,
    fall_rating: 0,
    longevity: 1,
    sillage: 1,
    day_rating: 0,
    night_rating: 0,
    overall_rating: 1,
    personal_notes: '',
    is_favorite: false,
    is_decant: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');
  const [houseSuggestions, setHouseSuggestions] = useState<string[]>([]);
  const [notesSuggestions, setNotesSuggestions] = useState<string[]>([]);
  const [showHouseSuggestions, setShowHouseSuggestions] = useState(false);
  const [showNotesSuggestions, setShowNotesSuggestions] = useState(false);
  const [activeNotesField, setActiveNotesField] = useState<string>('');

  // Load existing fragrance data if editing
  useEffect(() => {
    if (fragrance) {
      setFormData({
        house: fragrance.house || '',
        name: fragrance.name || '',
        concentration: fragrance.concentration || '',
        size_oz: fragrance.size_oz || 0,
        size_ml: fragrance.size_ml || 0,
        notes_top: fragrance.notes_top || '',
        notes_middle: fragrance.notes_middle || '',
        notes_base: fragrance.notes_base || '',
        is_clone: fragrance.is_clone || false,
        original_fragrance: fragrance.original_fragrance || '',
        winter_rating: fragrance.winter_rating || 0,
        spring_rating: fragrance.spring_rating || 0,
        summer_rating: fragrance.summer_rating || 0,
        fall_rating: fragrance.fall_rating || 0,
        longevity: fragrance.longevity || 1,
        sillage: fragrance.sillage || 1,
        day_rating: fragrance.day_rating || 0,
        night_rating: fragrance.night_rating || 0,
        overall_rating: fragrance.overall_rating || 1,
        personal_notes: fragrance.personal_notes || '',
        is_favorite: fragrance.is_favorite || false,
        is_decant: fragrance.is_decant || false,
      });
    }
  }, [fragrance]);

  // Load autocomplete suggestions
  useEffect(() => {
    const loadHouseSuggestions = async () => {
      if (formData.house.length > 0) {
        const suggestions = await getAutocompleteSuggestions('house', formData.house);
        setHouseSuggestions(suggestions);
      }
    };
    loadHouseSuggestions();
  }, [formData.house, getAutocompleteSuggestions]);

  const loadNotesSuggestions = async (query: string) => {
    // Extract the last note being typed (after the last comma)
    const notes = query.split(',');
    const lastNote = notes[notes.length - 1].trim();
    
    if (lastNote.length > 1) {
      const suggestions = await getAutocompleteSuggestions('note', lastNote);
      setNotesSuggestions(suggestions);
      setShowNotesSuggestions(true);
    } else {
      setShowNotesSuggestions(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    let newValue = type === 'checkbox' ? checked : value;
    let updateData: any = { [name]: newValue };

    // Handle size conversion
    if (name === 'size_oz' && value) {
      const ozValue = parseFloat(value);
      if (!isNaN(ozValue)) {
        updateData.size_ml = ozToMl(ozValue); // Convert oz to ml using standard sizes
      }
    } else if (name === 'size_ml' && value) {
      const mlValue = parseFloat(value);
      if (!isNaN(mlValue)) {
        updateData.size_oz = mlToOz(mlValue); // Convert ml to oz using standard sizes
      }
    }
    
    setFormData(prev => ({
      ...prev,
      ...updateData
    }));
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear general error
    if (generalError) {
      setGeneralError('');
    }

    // Handle autocomplete for house
    if (name === 'house') {
      setShowHouseSuggestions(value.length > 0);
    }

    // Handle autocomplete for notes
    if (name.includes('notes_')) {
      setActiveNotesField(name);
      loadNotesSuggestions(value);
    }
  };

  const handleSliderChange = (name: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field error
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSuggestionSelect = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setShowHouseSuggestions(false);
    setShowNotesSuggestions(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.house.trim()) {
      newErrors.house = 'House is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Fragrance name is required';
    }

    // Validate numeric fields
    if (formData.size_oz && (isNaN(Number(formData.size_oz)) || Number(formData.size_oz) < 0)) {
      newErrors.size_oz = 'Size in oz must be a valid positive number';
    }

    if (formData.size_ml && (isNaN(Number(formData.size_ml)) || Number(formData.size_ml) < 0)) {
      newErrors.size_ml = 'Size in ml must be a valid positive number';
    }

    // Ratings are now handled by sliders, so no validation needed
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Convert values for submission
    const submissionData = {
      ...formData,
      size_oz: formData.size_oz || undefined,
      size_ml: formData.size_ml || undefined,
      winter_rating: formData.winter_rating || undefined,
      spring_rating: formData.spring_rating || undefined,
      summer_rating: formData.summer_rating || undefined,
      fall_rating: formData.fall_rating || undefined,
      longevity: formData.longevity || undefined,
      sillage: formData.sillage || undefined,
      day_rating: formData.day_rating || undefined,
      night_rating: formData.night_rating || undefined,
      overall_rating: formData.overall_rating || undefined,
    };

    const result = fragrance 
      ? await updateFragrance(fragrance.id, submissionData)
      : await createFragrance(submissionData);
    
    if (result.success) {
      onSuccess();
      onClose();
    } else {
      setGeneralError(result.message);
    }
  };

  const isEditing = !!fragrance;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Fragrance' : 'Add New Fragrance'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {generalError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
              >
                <p className="text-red-600 dark:text-red-400 text-sm">{generalError}</p>
              </motion.div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <Input
                  name="house"
                  type="text"
                  label="House *"
                  placeholder="e.g., Chanel, Dior, Tom Ford"
                  value={formData.house}
                  onChange={handleInputChange}
                  onFocus={() => setShowHouseSuggestions(formData.house.length > 0)}
                  onBlur={() => setTimeout(() => setShowHouseSuggestions(false), 200)}
                  error={errors.house}
                  disabled={isLoading}
                />
                
                {/* House Suggestions */}
                {showHouseSuggestions && houseSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {houseSuggestions.map((house, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionSelect('house', house)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm"
                      >
                        {house}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Input
                name="name"
                type="text"
                label="Fragrance Name *"
                placeholder="e.g., Bleu de Chanel, Sauvage"
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name}
                disabled={isLoading}
              />
            </div>

            {/* Concentration and Size */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Concentration
                </label>
                <select
                  name="concentration"
                  value={formData.concentration}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select concentration</option>
                  <option value="EdC">Eau de Cologne</option>
                  <option value="EdT">Eau de Toilette</option>
                  <option value="EdP">Eau de Parfum</option>
                  <option value="Parfum">Parfum</option>
                  <option value="Elixir">Elixir</option>
                  <option value="Extrait">Extrait de Parfum</option>
                </select>
              </div>

              <Input
                name="size_oz"
                type="number"
                step="0.01"
                label="Size (oz)"
                placeholder="e.g., 3.4"
                value={formData.size_oz}
                onChange={handleInputChange}
                error={errors.size_oz}
                disabled={isLoading}
              />

              <Input
                name="size_ml"
                type="number"
                step="0.1"
                label="Size (ml)"
                placeholder="e.g., 100"
                value={formData.size_ml}
                onChange={handleInputChange}
                error={errors.size_ml}
                disabled={isLoading}
              />
            </div>

            {/* Notes */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notes</h3>
              
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Top Notes
                </label>
                <textarea
                  name="notes_top"
                  placeholder="e.g., Bergamot, Lemon, Pink Pepper"
                  value={formData.notes_top}
                  onChange={handleInputChange}
                  onFocus={() => {
                    setActiveNotesField('notes_top');
                    loadNotesSuggestions(formData.notes_top);
                  }}
                  onBlur={() => setTimeout(() => setShowNotesSuggestions(false), 200)}
                  disabled={isLoading}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
                
                {/* Notes Suggestions */}
                {showNotesSuggestions && activeNotesField === 'notes_top' && notesSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {notesSuggestions.map((note, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          const currentNotes = formData.notes_top;
                          const notes = currentNotes.split(',').map(n => n.trim());
                          notes[notes.length - 1] = note; // Replace the last note being typed
                          const newNotes = notes.join(', ');
                          handleSuggestionSelect('notes_top', newNotes);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm"
                      >
                        {note}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Middle Notes
                </label>
                <textarea
                  name="notes_middle"
                  placeholder="e.g., Rose, Jasmine, Geranium"
                  value={formData.notes_middle}
                  onChange={handleInputChange}
                  onFocus={() => {
                    setActiveNotesField('notes_middle');
                    loadNotesSuggestions(formData.notes_middle);
                  }}
                  onBlur={() => setTimeout(() => setShowNotesSuggestions(false), 200)}
                  disabled={isLoading}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
                
                {showNotesSuggestions && activeNotesField === 'notes_middle' && notesSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {notesSuggestions.map((note, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          const currentNotes = formData.notes_middle;
                          const notes = currentNotes.split(',').map(n => n.trim());
                          notes[notes.length - 1] = note;
                          const newNotes = notes.join(', ');
                          handleSuggestionSelect('notes_middle', newNotes);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm"
                      >
                        {note}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Base Notes
                </label>
                <textarea
                  name="notes_base"
                  placeholder="e.g., Sandalwood, Vanilla, Musk"
                  value={formData.notes_base}
                  onChange={handleInputChange}
                  onFocus={() => {
                    setActiveNotesField('notes_base');
                    loadNotesSuggestions(formData.notes_base);
                  }}
                  onBlur={() => setTimeout(() => setShowNotesSuggestions(false), 200)}
                  disabled={isLoading}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
                
                {showNotesSuggestions && activeNotesField === 'notes_base' && notesSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {notesSuggestions.map((note, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          const currentNotes = formData.notes_base;
                          const notes = currentNotes.split(',').map(n => n.trim());
                          notes[notes.length - 1] = note;
                          const newNotes = notes.join(', ');
                          handleSuggestionSelect('notes_base', newNotes);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm"
                      >
                        {note}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Ratings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ratings</h3>
              
              {/* Seasonal Ratings */}
              <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">Seasonal Ratings (0-5)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Slider
                    label="❄️ Winter"
                    value={formData.winter_rating}
                    onChange={(value) => handleSliderChange('winter_rating', value)}
                    min={0}
                    max={5}
                    step={0.25}
                    disabled={isLoading}
                  />
                  <Slider
                    label="🌸 Spring"
                    value={formData.spring_rating}
                    onChange={(value) => handleSliderChange('spring_rating', value)}
                    min={0}
                    max={5}
                    step={0.25}
                    disabled={isLoading}
                  />
                  <Slider
                    label="☀️ Summer"
                    value={formData.summer_rating}
                    onChange={(value) => handleSliderChange('summer_rating', value)}
                    min={0}
                    max={5}
                    step={0.25}
                    disabled={isLoading}
                  />
                  <Slider
                    label="🍂 Fall"
                    value={formData.fall_rating}
                    onChange={(value) => handleSliderChange('fall_rating', value)}
                    min={0}
                    max={5}
                    step={0.25}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Performance Ratings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Slider
                  label="⏱️ Longevity"
                  value={formData.longevity}
                  onChange={(value) => handleSliderChange('longevity', value)}
                  min={1}
                  max={5}
                  step={1}
                  disabled={isLoading}
                  valueLabels={['Very Weak', 'Weak', 'Moderate', 'Long Lasting', 'Eternal']}
                />
                <Slider
                  label="💨 Sillage"
                  value={formData.sillage}
                  onChange={(value) => handleSliderChange('sillage', value)}
                  min={1}
                  max={4}
                  step={1}
                  disabled={isLoading}
                  valueLabels={['Intimate', 'Moderate', 'Strong', 'Enormous']}
                />
              </div>

              {/* Day/Night and Overall Ratings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Slider
                  label="☀️ Day Rating (0-5)"
                  value={formData.day_rating}
                  onChange={(value) => handleSliderChange('day_rating', value)}
                  min={0}
                  max={5}
                  step={0.25}
                  disabled={isLoading}
                />
                <Slider
                  label="🌙 Night Rating (0-5)"
                  value={formData.night_rating}
                  onChange={(value) => handleSliderChange('night_rating', value)}
                  min={0}
                  max={5}
                  step={0.25}
                  disabled={isLoading}
                />
                <Slider
                  label="⭐ Overall Rating (1-10)"
                  value={formData.overall_rating}
                  onChange={(value) => handleSliderChange('overall_rating', value)}
                  min={1}
                  max={10}
                  step={0.25}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Personal Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Personal Notes
              </label>
              <textarea
                name="personal_notes"
                placeholder="Your thoughts, impressions, occasions to wear, etc."
                value={formData.personal_notes}
                onChange={handleInputChange}
                disabled={isLoading}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
              />
            </div>

            {/* Toggles */}
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                <input
                  type="checkbox"
                  name="is_favorite"
                  checked={formData.is_favorite}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                  ❤️ Favorite
                </span>
              </label>

              <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                <input
                  type="checkbox"
                  name="is_decant"
                  checked={formData.is_decant}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                  🧪 Decant
                </span>
              </label>

              <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                <input
                  type="checkbox"
                  name="is_clone"
                  checked={formData.is_clone}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">
                  🔄 Clone/Inspired-by
                </span>
              </label>
            </div>
            {/* Clone Information */}
            <div className="space-y-4">
              {formData.is_clone && (
                <Input
                  name="original_fragrance"
                  type="text"
                  label="Original Fragrance"
                  placeholder="e.g., Creed Aventus"
                  value={formData.original_fragrance}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              )}
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading 
                  ? (isEditing ? 'Updating...' : 'Adding...') 
                  : (isEditing ? 'Update Fragrance' : 'Add Fragrance')
                }
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default FragranceForm;
